
import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Event } from '@/lib/types';

interface CalendarPreviewSectionProps {
  events: Event[];
  isLoading: boolean;
}

const CalendarPreviewSection: React.FC<CalendarPreviewSectionProps> = ({ events, isLoading }) => {
  // Generate some sample dates for the preview
  const today = new Date();
  const daysOfWeek = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

  const generatePreviewDates = () => {
    const dates = [];
    const currentDate = new Date(today);

    // Start from the beginning of the current week
    const dayOfWeek = currentDate.getDay();
    const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
    currentDate.setDate(diff);

    // Generate 14 days
    for (let i = 0; i < 14; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  const previewDates = generatePreviewDates();

  return (
    <section className="py-12 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="bg-medihub-primary/10 p-3 rounded-full mb-4">
            <Calendar className="h-8 w-8 text-medihub-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Növelje eseményei láthatóságát egyetlen platformon
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            Segítünk, hogy az Ön által szervezett egészségügyi szakmai események könnyebben
            elérhetők legyenek a célközönség számára. Egyszerűen töltheti fel eseményeit,
            mi pedig gondoskodunk róla, hogy minél több érdeklődő szakember értesüljön róluk.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-4xl"
        >
          <Card className="overflow-hidden shadow-xl border border-gray-100">
            <CardContent className="p-0">
              {/* Calendar header */}
              <div className="bg-accent text-white px-6 py-4 flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <h3 className="font-medium">Egészségügyi eseménynaptár</h3>
                </div>
              </div>

              {/* Calendar days header */}
              <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                {daysOfWeek.map(day => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 bg-white">
                {previewDates.map((date, index) => {
                  const isToday = date.toDateString() === today.toDateString();
                  const hasEvent = events.some(event =>
                    new Date(event.dateStart).toDateString() === date.toDateString()
                  );

                  return (
                    <div
                      key={index}
                      className={`aspect-square p-1 border-b border-r border-gray-100 relative ${isToday ? 'bg-medihub-primary/5' : ''
                        }`}
                    >
                      <div className="h-full w-full flex flex-col">
                        <span className={`text-xs font-medium self-end p-1 rounded-full ${isToday ? 'bg-medihub-primary text-white' : 'text-gray-500'
                          }`}>
                          {date.getDate()}
                        </span>

                        {/* Event indicator */}
                        {hasEvent && (
                          <div className="mt-auto">
                            <div className="bg-medihub-accent h-1.5 rounded-full mx-1 mb-1"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Calendar footer with blurred effect to indicate there's more */}
              <div className="bg-gradient-to-t from-white to-transparent absolute bottom-0 left-0 right-0 h-20 pointer-events-none"></div>
            </CardContent>
          </Card>

          {/* Overlay with CTA */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center">
            <Link to="/calendar">
              <Button className="bg-medihub-accent hover:bg-medihub-accent/90 shadow-lg flex items-center gap-2">
                Teljes naptár megtekintése
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </motion.div>

        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            A MediHub naptára segít az egészségügyi szakembereknek könnyedén megtalálni a releváns eseményeket és
            a rendezvényszervezőknek elkerülni az ütközéseket más fontos eseményekkel.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CalendarPreviewSection;
